import * as Yup from 'yup';
import addProjectFormModel from './AddProjectFormModel';

const {
  formField: {
    projectTitle,
    planningDocument,
    isProjectStudy,
    isClearLanes,
    regions,
    strategyTypes,
    projectType,
    projectTo,
    projectFrom,
    website,
    description,
    projectDetails,
    feasibilityStudy,
    preliminaryDesign,
    nepaReview,
    finalDesign,
    funding,
    rowAcquisition,
    construction,
    scopeIdentified,
    fundingAcquired,
    researcherIdentified,
    studyInProgress,
    studyComplete,
    connectedRoadways,
    notes,
    status,
    reviewedDate,
    reviewedBy,
    createdDate,
    lastModifiedDate,
    lastModifiedBy
  }
} = addProjectFormModel;

// Treat a blank field as "no value" instead of NaN
const emptyToUndefined = (value, originalValue) =>
  typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : value;

// Number field with a friendly "numbers only" message
const costField = (field, isRequired) => {
  const base = Yup.number()
    .transform(emptyToUndefined)
    .typeError(field.typeErrorMsg);

  return isRequired
    ? base.required(field.requiredErrorMsg)
    : base.nullable().notRequired();
};

export default [
  // Step 1: General Project Details
  Yup.object().shape({
    [projectTitle.name]: Yup.string().max(255).required(`${projectTitle.requiredErrorMsg}`),
    [planningDocument.name]: Yup.string().required(`${planningDocument.requiredErrorMsg}`),
    [isProjectStudy.name]: Yup.string().required(`${isProjectStudy.requiredErrorMsg}`),
    [isClearLanes.name]: Yup.mixed().oneOf([true, false], `${isClearLanes.requiredErrorMsg}`).required(`${isClearLanes.requiredErrorMsg}`),
    [regions.name]: Yup.array().min(1, `${regions.requiredErrorMsg}`),
    [strategyTypes.name]: Yup.array().min(1, `${strategyTypes.requiredErrorMsg}`),
    [projectType.name]: Yup.string().required(`${projectType.requiredErrorMsg}`),
    [projectTo.name]: Yup.string(),
    [projectFrom.name]: Yup.string(),
    [website.name]: Yup.string(),
  }),

  // Step 2: Project Description and Details
  Yup.lazy((values) => {
    const isStudyProject = typeof values?.isProjectStudy === 'boolean'
      ? values.isProjectStudy
      : String(values?.isProjectStudy || '').toLowerCase() === 'yes';

    return Yup.object().shape({
      [description.name]: Yup.string().required(`${description.requiredErrorMsg}`),
      projectDetails: Yup.array()
        .of(
          Yup.object().shape({
            [projectDetails.csjNumber.name]: Yup.string(),
            [projectDetails.letDate.name]: Yup.date().nullable(),
            [projectDetails.completionDate.name]: Yup.string().nullable(),
            [projectDetails.costEstimate.name]: costField(projectDetails.costEstimate, !isStudyProject),
            [projectDetails.studyCost.name]: isStudyProject
              ? costField(projectDetails.studyCost, true)
              : Yup.mixed().notRequired(),
          })
        )
        .required('Must have at least one project detail')
        .min(1, 'Minimum 1 project detail'),
    });
  }),

  // Step 3: Conditional Fields Based on isProjectStudy
  Yup.lazy((values) => {
    const isStudyProject = typeof values?.isProjectStudy === 'boolean'
      ? values.isProjectStudy
      : String(values?.isProjectStudy || '').toLowerCase() === 'yes';

    if (isStudyProject) {
      return Yup.object().shape({

        [scopeIdentified.name]: Yup.string().required('This field is required'),
        [fundingAcquired.name]: Yup.string().required('This field is required'),
        [researcherIdentified.name]: Yup.string().required('This field is required'),
        [studyInProgress.name]: Yup.string().required('This field is required'),
        [studyComplete.name]: Yup.string().required('This field is required'),
      });
    } else {
      return Yup.object().shape({
        [feasibilityStudy.name]: Yup.string().required('This field is required'),
        [preliminaryDesign.name]: Yup.string().required('This field is required'),
        [nepaReview.name]: Yup.string().required('This field is required'),
        [finalDesign.name]: Yup.string().required('This field is required'),
        [funding.name]: Yup.string().required('This field is required'),
        [rowAcquisition.name]: Yup.string().required('This field is required'),
        [construction.name]: Yup.string().required('This field is required'),

      });
    }
  }),

  // Step 4: Connected Roadways
  Yup.object().shape({
    connectedRoadways: Yup.array()
      .of(
        Yup.object().shape({
          [connectedRoadways.segmentId.name]: Yup.string(),
          [connectedRoadways.roadName.name]: Yup.string(),
          [connectedRoadways.fromRoad.name]: Yup.string(),
          [connectedRoadways.toRoad.name]: Yup.string(),
          [connectedRoadways.rank.name]: Yup.string(),
          [connectedRoadways.impactArea.name]: Yup.string(),
        })
      )
      .required('Must have at least one roadway detail')
      .min(1, 'Minimum 1 roadway detail'),
  }),

  // Step 5: Additional Notes and Metadata
  Yup.object().shape({
    [notes.name]: Yup.string(),
    [status.name]: Yup.string(),
    [reviewedDate.name]: Yup.date(),
    [reviewedBy.name]: Yup.string(),
    [createdDate.name]: Yup.date(),
    [lastModifiedDate.name]: Yup.date(),
    [lastModifiedBy.name]: Yup.string(),
  }),
];
